import React from "react";
import { Box, Flex, useColorModeValue } from "@chakra-ui/react";

import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import type { BrokerTradeSyncInfo } from "../../utils/brokerTradeSync";
import { formatBrokerSyncDateRange } from "../../utils/brokerTradeSync";
import { BROKER_ALERT_DESC_CLASS, BROKER_ALERT_TITLE_CLASS } from "./brokerAlertUi";

export interface BrokerHistoryPathChoiceProps {
  tradeSync: Pick<
    BrokerTradeSyncInfo,
    "syncLookbackDays" | "syncFromDate" | "syncToDate"
  >;
  /** e.g. "Binance API sync" or "IBKR Flex sync" */
  syncLabel: string;
  /** e.g. "Broker Sync" or "Flex sync" */
  recentPathLabel: string;
  /** When false (e.g. cTrader `has_fixed_calendar_window`), omit rolling date ranges in copy. */
  showSyncDateRange?: boolean;
  /** Plain omits the bordered card shell for use inside the broker details card. */
  variant?: "card" | "plain";
  onChooseRecentOnly: () => void;
  onChooseNeedsCsv: () => void;
}

const CHOICE_SECONDARY_TEXT_CLASS =
  "font-[family-name:var(--font-primary)] text-sm leading-5 text-gray-500";

const ChoiceButtonLabel = ({
  title,
  subtitle,
  variant = "secondary",
}: {
  title: string;
  subtitle: string;
  variant?: "primary" | "secondary";
}): React.ReactElement => (
  <span className="flex w-full flex-col items-center gap-1 text-center">
    <span
      className={cn(
        CHOICE_SECONDARY_TEXT_CLASS,
        "font-medium",
        variant === "primary" && "text-white"
      )}
    >
      {title}
    </span>
    <span
      className={cn(
        CHOICE_SECONDARY_TEXT_CLASS,
        "font-normal",
        variant === "primary" && "text-white/95"
      )}
    >
      {subtitle}
    </span>
  </span>
);

const PRIMARY_GRADIENT = "linear-gradient(135deg, #38BBD1, #3EEAEC)";

const BrokerHistoryPathChoice = ({
  tradeSync,
  syncLabel,
  recentPathLabel,
  showSyncDateRange = true,
  variant = "card",
  onChooseRecentOnly,
  onChooseNeedsCsv,
}: BrokerHistoryPathChoiceProps): React.ReactElement => {
  const cardBg = useColorModeValue("white", "#151517");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const secondaryHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const rangeLabel = formatBrokerSyncDateRange(tradeSync.syncFromDate, tradeSync.syncToDate);

  const content = (
    <div className="flex w-full flex-col gap-4">
      <Field className="gap-2">
        <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>
          How far back do you need trades?
        </FieldLabel>
        <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
          {syncLabel} can automatically import trades from the last{" "}
          <span className="font-semibold">{tradeSync.syncLookbackDays} days</span>
          {showSyncDateRange ? <> ({rangeLabel})</> : null}. Choose the path that matches your
          history.
        </FieldDescription>
      </Field>

      <Field className="gap-2" data-broker-history-path-choice="">
        <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>
          Do you need trading data older than the last {tradeSync.syncLookbackDays} days?
        </FieldLabel>
        <Flex direction={{ base: "column", sm: "row" }} gap={3} w="100%">
          <Box flex={{ base: "none", sm: 1 }} width={{ base: "100%", sm: "auto" }}>
            <Box
              as="button"
              type="button"
              width="100%"
              py={3}
              px={4}
              borderRadius="md"
              border="none"
              cursor="pointer"
              bgGradient={PRIMARY_GRADIENT}
              color="white"
              fontFamily="'IBM Plex Sans', sans-serif"
              boxShadow="0 2px 8px rgba(56, 187, 209, 0.35)"
              transition="opacity 0.2s ease"
              _hover={{ opacity: 0.9 }}
              _active={{ bgGradient: PRIMARY_GRADIENT, opacity: 0.95 }}
              onClick={onChooseRecentOnly}
            >
              <ChoiceButtonLabel
                variant="primary"
                title="No, recent trades only"
                subtitle={
                  showSyncDateRange
                    ? `Use ${recentPathLabel} (${rangeLabel})`
                    : `Use ${recentPathLabel} (Open API)`
                }
              />
            </Box>
          </Box>
          <Box flex={{ base: "none", sm: 1 }} width={{ base: "100%", sm: "auto" }}>
            <Box
              as="button"
              type="button"
              data-broker-history-path-option="older"
              width="100%"
              py={3}
              px={4}
              borderRadius="md"
              borderWidth="1px"
              borderStyle="solid"
              borderColor={borderColor}
              cursor="pointer"
              bg={cardBg}
              fontFamily="'IBM Plex Sans', sans-serif"
              _hover={{ bg: secondaryHoverBg }}
              onClick={onChooseNeedsCsv}
            >
              <ChoiceButtonLabel
                variant="secondary"
                title="Yes, I need older history"
                subtitle="Start with CSV import, then sync recent trades"
              />
            </Box>
          </Box>
        </Flex>
      </Field>
    </div>
  );

  if (variant === "plain") {
    return content;
  }

  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      bg={cardBg}
      p={{ base: 4, md: 5 }}
    >
      {content}
    </Box>
  );
};

export default BrokerHistoryPathChoice;
